import React, { useState } from "react";
import {
  Grid,
  Typography,
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
} from "@mui/material";
import DesktopWindowsIcon from "@mui/icons-material/DesktopWindows";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import JumboCardQuick from "@jumbo/components/JumboCardQuick";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { toggleDeviceMaintenance } from "app/services/api/management";

const DSOK_FE_DOMAIN = process.env.REACT_APP_DSOK_FE_DOMAIN || "gastrokiosk.online";

const getPreviewUrl = (device, customerInfo) => {
  const { frontendType, customerId, code, beDomain } = customerInfo;
  const uuid = device.uuid || device.key;
  const isStaging = (beDomain || "").includes("staging");

  if (frontendType === "easyPay") {
    return `https://easypay.gastrokiosk.online/?customerId=${customerId}&deviceId=${uuid}`;
  }
  if (frontendType === "smartKiosk") {
    return isStaging
      ? `https://dsok-staging.smartkiosk.sk/?customerId=${customerId}&deviceId=${uuid}`
      : `https://dsok.smartkiosk.sk/?customerId=${customerId}&deviceId=${uuid}`;
  }
  if (frontendType === "easyFood") {
    return isStaging
      ? `https://easyfood-staging.smartkiosk.sk/?customerId=${customerId}&deviceId=${uuid}`
      : `https://easyfood.smartkiosk.sk/?customerId=${customerId}&deviceId=${uuid}`;
  }
  // default / gastroKiosk
  return `https://${code}.${DSOK_FE_DOMAIN}/?kioskId=${uuid}`;
};

const isInMaintenance = (device) =>
  device?.maintenance === true || device?.forceMaintenance === true;

const KioskMaintenanceWidget = () => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [customerInfo] = useState(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const dsokCustomer = user?.customer?.dsokSettings?.customer || {};
    return {
      frontendType: dsokCustomer.frontendType || "",
      customerId: user?.customer?._id || "",
      code: user?.customer?.code || "",
      beDomain: dsokCustomer.beDomain || "",
    };
  });

  const [devices, setDevices] = useState(() => {
    const user = JSON.parse(localStorage.getItem("user") || "{}");
    const devicesRaw =
      user?.customer?.dsokSettings?.customer?.devices || {};
    return Object.entries(devicesRaw)
      .filter(([, device]) => device?.demoMode !== true)
      .filter(([, device]) => {
        const type = String(device?.deviceType ?? "kiosk").toLowerCase();
        return type !== "pos";
      })
      .map(([key, device]) => ({ key, ...device }));
  });

  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    device: null,
  });
  const [loadingKey, setLoadingKey] = useState(null);

  const handleToggleClick = (device) => {
    setConfirmDialog({ open: true, device });
  };

  const handleCancel = () => {
    setConfirmDialog({ open: false, device: null });
  };

  const handleConfirm = async () => {
    const { device } = confirmDialog;
    setConfirmDialog({ open: false, device: null });
    setLoadingKey(device.key);

    const newState = !isInMaintenance(device);

    try {
      await toggleDeviceMaintenance(device.key, newState);
      setDevices((prev) =>
        prev.map((d) =>
          d.key === device.key
            ? { ...d, maintenance: newState, forceMaintenance: newState }
            : d,
        ),
      );
      enqueueSnackbar(t("notifications.wasSuccessful"), { variant: "success" });
    } catch {
      enqueueSnackbar(t("notifications.wasFailed"), { variant: "error" });
    } finally {
      setLoadingKey(null);
    }
  };

  const pendingDevice = confirmDialog.device;
  const pendingInMaintenance = pendingDevice ? isInMaintenance(pendingDevice) : false;

  return (
    <>
      <Grid item xs={12} md={8}>
        <JumboCardQuick
          title={
            <Typography variant="h2">
              {t("widgets.kioskMaintenance.title", "Kiosky")}
            </Typography>
          }
          headerSx={{ mb: 0, pb: 0 }}
          sx={{ height: "100%" }}
        >
          {devices.length === 0 ? (
            <Box sx={{ py: 3, textAlign: "center" }}>
              <Typography color="text.secondary" variant="body2">
                {t("widgets.kioskMaintenance.noDevices", "Žiadne kiosky")}
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2} sx={{ pt: 1 }}>
              {devices.map((device) => {
                const inMaintenance = isInMaintenance(device);
                const isLoading = loadingKey === device.key;
                return (
                  <Grid item key={device.key} xs={6} sm={4} md={3}>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        gap: 1.5,
                        p: 2,
                        border: "1px solid",
                        borderColor: inMaintenance ? "error.light" : "success.light",
                        borderRadius: 2,
                        backgroundColor: inMaintenance
                          ? "error.lighter"
                          : "success.lighter",
                        height: "100%",
                      }}
                    >
                      <DesktopWindowsIcon
                        sx={{
                          fontSize: 48,
                          color: inMaintenance ? "error.main" : "success.main",
                        }}
                      />
                      <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                        <Typography
                          variant="body2"
                          fontWeight={600}
                          textAlign="center"
                          sx={{ lineHeight: 1.3 }}
                        >
                          {device.deviceName || `Kiosk ${device.key}`}
                        </Typography>
                        <Tooltip title={t("widgets.kioskMaintenance.openPreview", "Otvoriť náhľad")}>
                          <IconButton
                            size="small"
                            onClick={() =>
                              window.open(
                                getPreviewUrl(device, customerInfo),
                                "_blank",
                                "noopener,noreferrer"
                              )
                            }
                            sx={{ color: "text.secondary", p: 0.25 }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Box>
                      <Button
                        variant="contained"
                        color={inMaintenance ? "success" : "error"}
                        disabled={isLoading}
                        onClick={() => handleToggleClick(device)}
                        fullWidth
                        sx={{ mt: "auto", minHeight: 44 }}
                      >
                        {isLoading ? (
                          <CircularProgress size={18} sx={{ color: "inherit" }} />
                        ) : inMaintenance ? (
                          t("widgets.kioskMaintenance.activate", "Aktivovať")
                        ) : (
                          t("widgets.kioskMaintenance.setMaintenance", "Údržba")
                        )}
                      </Button>
                    </Box>
                  </Grid>
                );
              })}
            </Grid>
          )}
        </JumboCardQuick>
      </Grid>

      <Dialog open={confirmDialog.open} onClose={handleCancel} maxWidth="xs" fullWidth>
        <DialogTitle>
          {pendingInMaintenance
            ? t("widgets.kioskMaintenance.confirmActivateTitle", "Aktivovať kiosk?")
            : t("widgets.kioskMaintenance.confirmMaintenanceTitle", "Prepnúť do údržby?")}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {pendingInMaintenance
              ? t("widgets.kioskMaintenance.confirmActivateText", {
                  name: pendingDevice?.deviceName || pendingDevice?.key,
                })
              : t("widgets.kioskMaintenance.confirmMaintenanceText", {
                  name: pendingDevice?.deviceName || pendingDevice?.key,
                })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancel} color="inherit" sx={{ minHeight: 44 }}>
            {t("buttons.cancel", "Zrušiť")}
          </Button>
          <Button
            onClick={handleConfirm}
            variant="contained"
            autoFocus
            sx={{ minHeight: 44 }}
          >
            {t("buttons.confirm", "Potvrdiť")}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default KioskMaintenanceWidget;
